<template>
    <div class="flex flex-col">
        <label v-if="showLabel" :for="label" class="flex justify-between font-semibold">
            {{ label }}
            <span v-if="!required && showOptionalLabel" class="font-normal">{{
                $t('form.optional')
            }}</span>
        </label>
        <div class="relative w-full">
            <SvgComponent v-if="icon !== undefined" class="absolute top-3.5 left-2" :icon="icon" />
            <input
                :id="label"
                ref="input"
                v-model="model"
                :name="name"
                :type="type"
                :max="type === 'date' ? getCurrentDate() : undefined"
                :min="type === 'number' ? 1 : undefined"
                :placeholder="placeholder"
                :class="['input-field', variant[sizeVariant], { 'pl-8!': icon !== undefined }]"
                :required="required"
                :readonly="readonly" />
        </div>
        <HelperText
            v-if="validationType !== 'none' && validationType !== undefined"
            class="mt-2"
            :icon="validationType"
            :variant="validationType"
            :text="validationMessage[validationType]" />
    </div>
</template>
<script lang="ts" setup>
import type { InputProps } from '~/types/input';

const {
    showLabel = true,
    icon = undefined,
    placeholder = '',
    sizeVariant = 'md',
    validationType = 'none',
    successMessage = '',
    infoMessage = '',
    errorMessage = '',
    warningMessage = '',
    required = false,
    showOptionalLabel = true,
    readonly = false,
    type,
} = defineProps<InputProps>();

const model = defineModel<number | string | null>();

const input = useTemplateRef<HTMLInputElement | null>('input');

const variant = {
    lg: 'input-lg',
    md: 'max-w-full',
    sm: 'max-w-45',
};

const validationMessage = computed(() => {
    return {
        error: errorMessage,
        success: successMessage ?? '',
        warning: warningMessage ?? '',
        info: infoMessage ?? '',
        none: '',
    };
});

const getCurrentDate = (): string => {
    const date = new Date();
    const part = date.toISOString().split('T')[0]; // Returns the date in YYYY-MM-DD format
    if (!part) {
        return '';
    }
    return part;
};

watch(
    () => validationType,
    () => {
        if (validationType === 'none') {
            input.value?.setCustomValidity('');
            return;
        }

        input.value?.setCustomValidity(errorMessage);
    }
);
</script>

<style lang="css">
@reference "~/assets/css/main.css";

.input-field {
    @apply text-brand-black border-brand-black-60 h-(--base-button-height) w-full rounded border px-2 py-[13px] text-base leading-4.5 focus:border-black focus:text-black;
}

input[type='date'] {
    @apply appearance-none;
}
</style>
